import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  pipelineService,
  ETAPAS_ATIVAS,
  CANAIS,
  etapaMeta,
  canalMeta,
  tipoAssetMeta,
  formatCurrency,
  type LeadEtapa,
} from '@/features/pipeline/services/pipeline.service';
import { ArrowUpRight, TrendingUp, Wallet, Users, CalendarCheck, Trophy, Target, type LucideIcon } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/pipeline/')({
  component: PipelineDashboardPage,
});

function PipelineDashboardPage() {
  return (
    <PermissionGate permission={Permission.PIPELINE_VIEW}>
      <PipelineDashboardContent />
    </PermissionGate>
  );
}

function PipelineDashboardContent() {
  const { data, isLoading } = useQuery({
    queryKey: ['pipeline-dashboard'],
    queryFn: pipelineService.getDashboard,
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Carregando...</div>;

  const { kpis, funil, porCanal, topAssets, atividadeRecente } = data ?? {
    kpis: {
      leadsAtivos: 0,
      reunioesMarcadas: 0,
      valorPropostasAbertas: 0,
      receitaFechada: 0,
      totalLeads: 0,
      taxaConversaoGeral: 0,
      totalInvestido: 0,
      cac: null,
    },
    funil: [],
    porCanal: [],
    topAssets: [],
    atividadeRecente: [],
  };

  // Mapa etapa → quantidade
  const byEtapa = Object.fromEntries(
    funil.map((f) => [f.etapa, f.count]),
  ) as Record<string, number>;

  const maxFunil = Math.max(...ETAPAS_ATIVAS.map((e) => byEtapa[e] ?? 0), 1);

  // Taxas de conversão entre etapas consecutivas
  const conv = (de: LeadEtapa, para: LeadEtapa) => {
    const totalAlemDe = ETAPAS_ATIVAS.slice(ETAPAS_ATIVAS.indexOf(de)).reduce(
      (s, e) => s + (byEtapa[e] ?? 0),
      0,
    );
    const totalAlemPara = ETAPAS_ATIVAS.slice(ETAPAS_ATIVAS.indexOf(para)).reduce(
      (s, e) => s + (byEtapa[e] ?? 0),
      0,
    );
    if (totalAlemDe === 0) return 0;
    return Math.round((totalAlemPara / totalAlemDe) * 100);
  };

  // Leads por canal (todos os canais, mesmo com 0)
  const porCanalMap = Object.fromEntries(porCanal.map((c) => [c.canal, c.count]));
  const canalData = CANAIS.map((c) => ({
    canal: c.label,
    qtd: porCanalMap[c.value] ?? 0,
  }));

  const chartOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: canalData.map((c) => c.canal),
      labels: { show: false },
    },
    grid: { show: false },
    colors: ['#6366f1', '#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'],
    tooltip: {
      y: { formatter: (val: number) => `${val} lead(s)` },
    },
  };

  const chartSeries = [{ name: 'Leads', data: canalData.map((c) => c.qtd) }];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pipeline de Vendas</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Visão geral do seu funil de vendas.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/pipeline/kanban" className="rounded-md border px-3 py-2 text-sm hover:bg-accent">
            Ver Kanban
          </Link>
          <Link to="/pipeline/leads" className="rounded-md border px-3 py-2 text-sm hover:bg-accent">
            Leads
          </Link>
          <Link
            to="/pipeline/leads/novo"
            className="rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm hover:bg-primary/90 inline-flex items-center gap-1"
          >
            Novo lead <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Users} label="Leads ativos" value={String(kpis.leadsAtivos)} />
        <Kpi icon={CalendarCheck} label="Reuniões marcadas" value={String(kpis.reunioesMarcadas)} />
        <Kpi icon={Wallet} label="Valor em propostas" value={formatCurrency(kpis.valorPropostasAbertas)} />
        <Kpi icon={Trophy} label="Receita fechada" value={formatCurrency(kpis.receitaFechada)} tone="success" />
        <Kpi
          icon={Target}
          label="CAC (custo por cliente fechado)"
          value={kpis.cac != null ? formatCurrency(kpis.cac) : '—'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Funil */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-primary" /> Funil de conversão
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {ETAPAS_ATIVAS.map((e, i) => {
              const meta = etapaMeta(e);
              const qtd = byEtapa[e] ?? 0;
              const width = `${Math.max((qtd / maxFunil) * 100, 4)}%`;
              return (
                <div key={e}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">
                      {i + 1}. {meta.label}
                    </span>
                    <span className="font-medium">{qtd}</span>
                  </div>
                  <div className="h-3 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width, background: meta.color }}
                    />
                  </div>
                </div>
              );
            })}

            <div className="grid grid-cols-4 gap-2 pt-4 border-t border-border mt-4 text-center">
              {[
                ['Lista→Contato', conv('lista', 'contato')],
                ['Contato→Reunião', conv('contato', 'reuniao')],
                ['Reunião→Proposta', conv('reuniao', 'proposta')],
                ['Proposta→Fechado', conv('proposta', 'fechamento')],
              ].map(([l, v]) => (
                <div key={l as string} className="rounded-md bg-secondary/50 p-2">
                  <div className="text-xs text-muted-foreground">{l}</div>
                  <div className="text-lg font-semibold text-primary">{v}%</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Canais */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Leads por canal</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            <Chart options={chartOptions} series={chartSeries} type="bar" height="100%" />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Atividade recente */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Atividade recente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {atividadeRecente.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma interação registrada ainda.</p>
            )}
            {atividadeRecente.map((i) => {
              const cm = canalMeta(i.canal);
              const Ic = cm.icon;
              return (
                <Link
                  key={i.id}
                  to="/pipeline/leads/$id"
                  params={{ id: i.leadId }}
                  className="flex items-start gap-3 rounded-md p-2 hover:bg-secondary/50 transition"
                >
                  <div className="grid h-8 w-8 place-items-center rounded-md bg-secondary shrink-0">
                    <Ic className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {i.leadNome ?? 'Lead'} <span className="text-muted-foreground">· {cm.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {i.mensagem ?? '(sem mensagem)'}
                    </div>
                    {i.assetTitulo && (
                      <Badge variant="outline" className="mt-1 text-[10px]">
                        Asset: {i.assetTitulo}
                      </Badge>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {format(new Date(i.createdAt), 'dd MMM HH:mm', { locale: ptBR })}
                  </span>
                </Link>
              );
            })}
          </CardContent>
        </Card>

        {/* Top assets */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Assets mais usados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {topAssets.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Nenhum asset ainda.{' '}
                <Link to="/pipeline/assets/novo" className="text-primary hover:underline">
                  Criar o primeiro
                </Link>
                .
              </p>
            )}
            {topAssets.map((a) => {
              const m = tipoAssetMeta(a.tipo);
              const Ic = m.icon;
              return (
                <Link
                  key={a.id}
                  to="/pipeline/assets/$id"
                  params={{ id: a.id }}
                  className="flex items-center gap-3 rounded-md p-2 hover:bg-secondary/50 transition"
                >
                  <div className="grid h-8 w-8 place-items-center rounded-md bg-secondary shrink-0">
                    <Ic className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.titulo}</div>
                    <div className="text-xs text-muted-foreground">{m.label}</div>
                  </div>
                  <Badge variant="secondary">{a.usoCount} usos</Badge>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  tone?: 'success';
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icon className={`h-4 w-4 ${tone === 'success' ? 'text-emerald-500' : 'text-primary'}`} />
        </div>
        <div className="mt-2 text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
